import {
  Box,
  Chip,
  Container,
  Link,
  TablePagination,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SuppliesHeader from "./SuppliesHeader/SuppliesHeader";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FormattedOwnerData } from "../../core/models/ownerData/ownerData.model";
import {
  FiltersChipsSupplies,
  FiltersSupplies,
  GetSuppliesRequestBody,
  GroupedSuppliesTableRow,
  TableOrder,
  TotalGroupedSupplies,
} from "../../core/models/supplies/supplies.model";
import { getGroupedSupplies } from "../../core/services/supplies/supplies.service";
import {
  formatSuppliesCups,
  getLocalstorageSupplyFilters,
  initialFiltersSupplies,
  removeLocalstorageSupplyFilters,
  setLocalstorageSupplyFilters,
} from "../../utils/components/supplies/supplies.util";
import SuppliesTable from "./SuppliesTable/SuppliesTable";
import { useTranslation } from "react-i18next";
import "./Supplies.scss";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import SuppliesFilters from "./SuppliesFilters/SuppliesFilters";
import SuppliesCard from "./SuppliesCard/SuppliesCard";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import EmptyStateComponent from "../UI/EmptyStateComponent/EmptyStateComponent";
import LoadingComponentComponent from "../UI/LoadingComponentComponent/LoadingComponentComponent";
import {
  ROWS_PER_PAGE_OPTIONS,
  TARIFF_OPTIONS,
} from "../../core/constants/constants";
import { FiltersCifCups } from "../../core/models/filtersCifCups/filtersCifCups.model";
import { formatTariffOptionLabel } from "../../utils/formatters/formatters.util";
import { isEmpty, isEqual, some } from "lodash";

export type SupplyEnviroment = "light" | "gas";

export interface SuppliesOrderState {
  orderBy?: keyof GroupedSuppliesTableRow;
  order?: TableOrder;
}

const Supplies = () => {
  const { t } = useTranslation();
  const { ownerData, userData } = useContext(TotalEnergiesContext);
  const savedFilters = getLocalstorageSupplyFilters();
  const isDesktopViewport = useMediaQuery("(min-width:1440px)");
  const [suppliesTableRows, setSuppliesTableRows] = useState<
    GroupedSuppliesTableRow[]
  >([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [filtersSupplies, setFiltersSupplies] = useState<FiltersSupplies>(
    savedFilters ? JSON.parse(savedFilters!) : initialFiltersSupplies
  );
  const [envirormentSelected, setEnvirormentSelected] =
    useState<SupplyEnviroment>("light");
  const [filtersChipsSupplies, setFiltersChipsSupplies] = useState<
    FiltersChipsSupplies[]
  >([]);
  const [totalSupplies, setTotalSupplies] = useState<TotalGroupedSupplies>({
    totalRegistrosQuery: null,
    totalSuministrosLuz: null,
    totalSuministrosGas: null,
    totalContratosLuz: null,
    totalContratosGas: null,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showButton, setShowButton] = useState(true);

  const [orderState, setOrderState] = useState<SuppliesOrderState>({
    order: "asc",
  });

  const showNoResults = useMemo(
    () =>
      !isLoading &&
      ((totalSupplies.totalSuministrosLuz === 0 &&
        envirormentSelected === "light") ||
        (totalSupplies.totalSuministrosGas === 0 &&
          envirormentSelected === "gas")),
    [
      envirormentSelected,
      isLoading,
      totalSupplies.totalSuministrosLuz,
      totalSupplies.totalSuministrosGas,
    ]
  );

  const getOrderDirection = useCallback(
    (orderBy: keyof GroupedSuppliesTableRow | undefined) => {
      if (orderState.orderBy === orderBy) {
        return orderState.order;
      } else {
        return undefined;
      }
    },
    [orderState.order, orderState.orderBy]
  );

  const getSuppliesTableRows = useCallback(
    (
      { isAdministrator, idClient, idAdministrator }: FormattedOwnerData,
      token: string,
      { cups, cif, location, socialReason, tariffs }: FiltersSupplies,
      page: number,
      rowsPerPage: number,
      envirormentSelected: SupplyEnviroment | null,
      abortSignal: AbortSignal
    ) => {
      const getSuppliesRequestBody: GetSuppliesRequestBody = {
        userid: isAdministrator ? idAdministrator : idClient,
        environmentCode:
          envirormentSelected === null
            ? null
            : envirormentSelected === "light"
            ? "luz"
            : "gas",
        isAdmin: isAdministrator,
        pageSize: rowsPerPage.toString(),
        pageIndex: page.toString(),
        city: location?.length ? location : null,
        cups,
        cif,
        razonSocial: socialReason,
        fees: tariffs,
        cupsCall: "true",
        Order: {
          Cups: getOrderDirection("CUPS"),
          // Entorno: getOrderDirection("envirorment"), // possible future implementation
          Identidad: getOrderDirection("cif"),
          PoblacionCUPS: getOrderDirection("town"),
          RazonSocial: getOrderDirection("socialReason"),
          // Tarifa: getOrderDirection("rate"), // possible future implementation
        },
      };

      getGroupedSupplies(getSuppliesRequestBody, token, abortSignal)
        .then(
          ({
            totalRegistrosQuery,
            totalSuministrosLuz,
            totalSuministrosGas,
            totalContratosLuz,
            totalContratosGas,
            cups,
          }) => {
            if (envirormentSelected) {
              setSuppliesTableRows(formatSuppliesCups(cups));
            } else if (
              envirormentSelected === null ||
              some(totalSupplies, isEmpty)
            ) {
              setTotalSupplies((prev) => ({
                totalRegistrosQuery:
                  prev.totalRegistrosQuery ?? totalRegistrosQuery,
                totalSuministrosLuz:
                  prev.totalSuministrosLuz ?? totalSuministrosLuz,
                totalSuministrosGas:
                  prev.totalSuministrosGas ?? totalSuministrosGas,
                totalContratosLuz: prev.totalContratosLuz ?? totalContratosLuz,
                totalContratosGas: prev.totalContratosGas ?? totalContratosGas,
              }));
            }
            return;
          }
        )
        .catch(() => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [getOrderDirection, totalSupplies]
  );

  useEffect(() => {
    const suppliesAbortController = new AbortController();
    const suppliesAbortController2 = new AbortController();

    if (ownerData && userData) {
      window.scrollTo({ top: 0, behavior: "auto" });
      setSuppliesTableRows([]);
      setIsLoading(true);
      if (Object.values(totalSupplies).some((value) => value === null)) {
        getSuppliesTableRows(
          ownerData,
          userData.token,
          filtersSupplies,
          page,
          rowsPerPage,
          null,
          suppliesAbortController2.signal
        );
      }
      getSuppliesTableRows(
        ownerData,
        userData.token,
        filtersSupplies,
        page,
        rowsPerPage,
        envirormentSelected,
        suppliesAbortController.signal
      );
      getFiltersChips(filtersSupplies);

      return () => {
        suppliesAbortController.abort();
        suppliesAbortController2.abort();
      };
    }
    // without "totalSupplies" to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    envirormentSelected,
    filtersSupplies,
    getSuppliesTableRows,
    ownerData,
    page,
    rowsPerPage,
    userData,
    orderState,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.body.scrollHeight;

      if (isAtBottom && !isDesktopViewport) {
        setShowButton(false);
      } else {
        setShowButton(true);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isDesktopViewport]);

  const getFiltersChips = (filtersSupplies: FiltersSupplies) => {
    setFiltersChipsSupplies([]);
    const formattedFiltersSupplies = {
      ...filtersSupplies,
      location: filtersSupplies.location?.length
        ? [filtersSupplies.location]
        : [],
    };

    for (const [key, values] of Object.entries(formattedFiltersSupplies)) {
      if (values.length) {
        setFiltersChipsSupplies((prevFiltersChipsSupplies) => [
          ...prevFiltersChipsSupplies,
          {
            filterSuppliesKey: key as keyof FiltersCifCups,
            totalValues: values.length,
            label: values[0],
          },
        ]);
      }
    }
  };

  const handleDeleteChip = (filterSuppliesKey: keyof FiltersCifCups) => {
    const newSuppliesFilters = {
      ...filtersSupplies,
      [filterSuppliesKey]: [],
    };

    setLocalstorageSupplyFilters(newSuppliesFilters);

    setFiltersSupplies(newSuppliesFilters);

    setFiltersChipsSupplies((prevFiltersChipsSupplies) =>
      prevFiltersChipsSupplies.filter(
        ({ filterSuppliesKey: prevFilterSuppliesKey }) =>
          prevFilterSuppliesKey !== filterSuppliesKey
      )
    );
  };

  const handleSetEnvirormentSelected = (enviroment: SupplyEnviroment) => {
    if (
      !isEqual(filtersSupplies, initialFiltersSupplies) &&
      !isEmpty(filtersSupplies.tariffs)
    ) {
      setFiltersSupplies((prevFilters) => {
        const updatedFilters = {
          ...prevFilters,
          tariffs: [],
        };
        setLocalstorageSupplyFilters(updatedFilters);
        return updatedFilters;
      });
    }
    setEnvirormentSelected(enviroment);
    setPage(0);
  };

  const tablePaginationCountValue = useMemo(() => {
    if (envirormentSelected === "gas") {
      return totalSupplies.totalSuministrosGas || 0;
    } else if (envirormentSelected === "light") {
      return totalSupplies.totalSuministrosLuz || 0;
    } else return 0;
  }, [
    envirormentSelected,
    totalSupplies.totalSuministrosGas,
    totalSupplies.totalSuministrosLuz,
  ]);

  const showPagination = useMemo(
    () =>
      Math.ceil((tablePaginationCountValue || 0) / rowsPerPage) !== 1 &&
      (tablePaginationCountValue || 0) >= rowsPerPage &&
      !isLoading,
    [isLoading, rowsPerPage, tablePaginationCountValue]
  );

  return (
    <Box component="section" className="supplies">
      <SuppliesHeader
        ownerData={ownerData}
        isDesktopViewport={isDesktopViewport}
        totalSupplies={totalSupplies}
        envirormentSelected={envirormentSelected}
        setEnvirormentSelected={handleSetEnvirormentSelected}
      />
      <Container
        maxWidth="xl"
        component="article"
        className="supplies__table-filters-container"
      >
        <Box
          component="div"
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gap={3}
          paddingBottom="1rem"
        >
          <Box component="div" flexBasis="100%">
            {showButton && (
              <ButtonElement
                onClick={() => setIsFiltersOpen(true)}
                secondary
                iconL
                iconName="t-icon-filter"
                disabled={
                  (!!!suppliesTableRows.length &&
                    !!!filtersChipsSupplies.length) ||
                  (!!!filtersChipsSupplies.length && showNoResults)
                }
              >
                {t("Tablas.Filtros")}{" "}
                {!!filtersChipsSupplies.length && (
                  <span className="supplies__filter-count">
                    {filtersChipsSupplies.length}
                  </span>
                )}
              </ButtonElement>
            )}
            {!!filtersChipsSupplies.length &&
              filtersChipsSupplies.map(
                ({ filterSuppliesKey, label, totalValues }) => (
                  <Chip
                    className="filter-chip"
                    key={filterSuppliesKey}
                    label={
                      <Typography>
                        {filterSuppliesKey === "tariffs"
                          ? formatTariffOptionLabel(label)
                          : label}{" "}
                        {totalValues > 1 && (
                          <span className="supplies__filter-count">
                            +{totalValues - 1}
                          </span>
                        )}
                      </Typography>
                    }
                    onDelete={() => {
                      handleDeleteChip(filterSuppliesKey);
                      // setPage(0);
                      // refreshLocalstorageSupplyFilters(filtersSupplies);
                    }}
                  />
                )
              )}
            {!!filtersChipsSupplies.length && (
              <Link
                component="button"
                onClick={() => {
                  setFiltersChipsSupplies([]);
                  setFiltersSupplies(initialFiltersSupplies);
                  removeLocalstorageSupplyFilters();
                  setPage(0);
                }}
              >
                {t("Tablas.Borrar todo")}
              </Link>
            )}
          </Box>
          <SuppliesFilters
            isFiltersOpen={isFiltersOpen}
            setIsFiltersOpen={setIsFiltersOpen}
            filtersSupplies={filtersSupplies}
            setFiltersSupplies={(filters) => {
              setLocalstorageSupplyFilters(filters);
              setFiltersSupplies(filters);
              setPage(0);
            }}
            ownerDataCups={ownerData?.cups || []}
            tariffOptions={
              envirormentSelected === "light"
                ? TARIFF_OPTIONS.electricity
                : TARIFF_OPTIONS.gas
            }
          />
        </Box>
      </Container>
      {!showNoResults && (
        <Container maxWidth="xl" component="article">
          {isDesktopViewport ? (
            <SuppliesTable
              orderState={orderState}
              setOrderState={setOrderState}
              suppliesTableRows={suppliesTableRows}
              isAdministrator={ownerData?.isAdministrator}
            />
          ) : (
            <SuppliesCard
              suppliesTableRows={suppliesTableRows}
              isAdministrator={ownerData?.isAdministrator}
            />
          )}
          {isLoading && (
            <Box padding={"2rem 0"}>
              <LoadingComponentComponent
                isLoading={isLoading}
                backgroundColor={"transparent"}
                position={"relative"}
                height={"4rem"}
              />
            </Box>
          )}
          {showPagination && (
            <TablePagination
              className="table-pagination"
              showFirstButton
              showLastButton
              component="div"
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage={t(`Tablas.Filas por página`)}
              labelDisplayedRows={({ page, count }) =>
                t("Tablas.Página de", {
                  actual: page + 1,
                  total: Math.ceil(count / rowsPerPage),
                })
              }
              count={tablePaginationCountValue}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(
                event: React.MouseEvent<HTMLButtonElement> | null,
                newPage: number
              ) => {
                setPage(newPage);
              }}
              onRowsPerPageChange={(
                event: React.ChangeEvent<HTMLInputElement>
              ) => {
                setRowsPerPage(+event.target.value);
                setPage(0);
              }}
            />
          )}
        </Container>
      )}
      {showNoResults && (
        <Container maxWidth="xl" component="article">
          <EmptyStateComponent text={"No hay resultados"} height="17rem" />
        </Container>
      )}
    </Box>
  );
};

export default Supplies;
